<template>
  <div>
    <div v-if="locationOptions && locationOptions.length" class="form-group mb-3">
      <label for="zaku-pos-upload-location" class="mb-1">
        <strong>{{ t('features.zaku_pos_upload.location.label', { default: 'Upload to location' }) }}</strong>
      </label>
      <select
        id="zaku-pos-upload-location"
        v-model="selectedLocation"
        class="form-control form-control-sm"
        style="max-width: 320px;"
      >
        <option v-for="opt in locationOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
      <small class="text-muted">
        {{ t('features.zaku_pos_upload.location.hint', { default: "InventoryQuantity values land at this location by default. A row's InventoryLocation cell (e.g. 'kappabashi', 'online') overrides this default for that row only." }) }}
      </small>
    </div>

    <div
      class="upload-dropzone p-4 mb-3 text-center border rounded"
      :class="{ 'border-primary bg-light': dragActive, 'border-secondary': !dragActive, 'has-file': !!file, 'is-dragging': dragActive }"
      :role="file ? null : 'button'"
      :tabindex="file ? -1 : 0"
      :aria-label="file ? null : t('features.zaku_pos_upload.dropzone.aria_label')"
      @dragenter.prevent="dragActive = true"
      @dragover.prevent="dragActive = true"
      @dragleave.prevent="dragActive = false"
      @drop.prevent="handleDrop"
      @click="openFilePicker"
      @keydown.enter.prevent="activateDropzone"
      @keydown.space.prevent="activateDropzone"
    >
      <input
        ref="fileInput"
        type="file"
        accept=".csv,text/csv"
        aria-hidden="true"
        tabindex="-1"
        class="d-none"
        @change="handleFileChange"
      >
      <div v-if="!file">
        <i class="fas fa-cloud-upload-alt fa-2x text-muted mb-2" aria-hidden="true"></i>
        <p class="mb-1" v-html="t('features.zaku_pos_upload.dropzone.prompt_html')"></p>
        <p class="mb-0">
          <button
            type="button"
            class="btn btn-link btn-sm p-0"
            :aria-description="t('features.zaku_pos_upload.dropzone.required_columns', { columns: requiredColumns.join(', ') })"
            @click.stop="showAllColumns = true"
          >
            {{ t('features.zaku_pos_upload.dropzone.help_cta') }}
          </button>
        </p>
      </div>
      <div v-else @click.stop>
        <p class="mb-1">
          <i class="fas fa-file-csv text-primary" aria-hidden="true"></i>
          <strong>{{ file.name }}</strong>
          <span class="text-muted small">({{ formatBytes(file.size) }})</span>
        </p>
        <p class="mb-0">
          <button type="button" class="btn btn-sm btn-outline-secondary mr-1" @click="openFilePicker">
            <i class="fas fa-exchange-alt" aria-hidden="true"></i> {{ t('features.zaku_pos_upload.dropzone.replace') }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="clearFile">
            <i class="fas fa-times" aria-hidden="true"></i> {{ t('features.zaku_pos_upload.dropzone.clear') }}
          </button>
        </p>
      </div>
    </div>

    <div class="mb-3">
      <a :href="csvTemplateUrl" class="btn btn-outline-secondary btn-sm">
        <i class="fas fa-download" aria-hidden="true"></i> {{ t('features.zaku_pos_upload.controls.download_template') }}
      </a>
      <a v-if="csvInventoryTemplateUrl" :href="csvInventoryTemplateUrl" class="btn btn-outline-secondary btn-sm ml-2">
        <i class="fas fa-download" aria-hidden="true"></i> {{ t('features.zaku_pos_upload.controls.download_inventory_template') }}
      </a>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm ml-2"
        @click="showAllColumns = !showAllColumns"
      >
        {{ showAllColumns ? t('features.zaku_pos_upload.controls.hide_columns') : t('features.zaku_pos_upload.controls.show_columns') }}
      </button>
    </div>

    <div v-if="showAllColumns" class="card card-body mb-3 p-2 columns-panel">
      <div class="mb-2 d-flex flex-wrap align-items-center">
        <input
          v-model="columnsSearch"
          type="search"
          name="zaku_pos_columns_search"
          autocomplete="off"
          spellcheck="false"
          class="form-control form-control-sm mr-3"
          style="max-width: 260px"
          :placeholder="t('features.zaku_pos_upload.columns_panel.search_placeholder')"
          :aria-label="t('features.zaku_pos_upload.columns_panel.search_aria')"
        >
        <a
          v-if="formatGuideUrl"
          :href="formatGuideUrl"
          target="_blank"
          rel="noopener"
          class="small"
        >
          {{ t('features.zaku_pos_upload.columns_panel.full_guide') }}
          <i class="fas fa-external-link-alt ml-1" aria-hidden="true"></i>
        </a>
      </div>
      <div class="table-responsive">
        <table class="table table-sm mb-0">
          <thead>
            <tr>
              <th>{{ t('features.zaku_pos_upload.columns_panel.column') }}</th>
              <th style="width: 110px">{{ t('features.zaku_pos_upload.columns_panel.required') }}</th>
              <th style="width: 200px">{{ t('features.zaku_pos_upload.columns_panel.example') }}</th>
              <th style="width: 220px">{{ t('features.zaku_pos_upload.columns_panel.format_allowed') }}</th>
              <th>{{ t('features.zaku_pos_upload.columns_panel.notes') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredSchema.length === 0">
              <td colspan="5" class="text-center text-muted small">{{ t('features.zaku_pos_upload.columns_panel.no_match') }}</td>
            </tr>
            <tr v-for="col in filteredSchema" :key="col.name">
              <td><code translate="no">{{ col.name }}</code></td>
              <td>
                <span v-if="col.required" class="badge badge-primary">{{ t('features.zaku_pos_upload.badges.required') }}</span>
                <span v-else-if="col.paired_with" class="badge badge-warning">
                  {{ t('features.zaku_pos_upload.badges.paired_with', { column: col.paired_with }) }}
                </span>
                <span v-else class="badge badge-secondary">{{ t('features.zaku_pos_upload.badges.optional') }}</span>
              </td>
              <td class="example">
                <code v-if="col.example" class="small" translate="no">{{ col.example }}</code>
                <span v-else class="text-muted small">—</span>
              </td>
              <td class="format small">
                <div v-if="col.format" translate="no">{{ col.format }}</div>
                <div v-if="col.allowed && col.allowed.length" translate="no">
                  <code v-for="value in col.allowed" :key="value" class="mr-1">{{ value }}</code>
                </div>
                <div v-if="col.range" translate="no">{{ col.range }}</div>
                <span v-if="!col.format && !(col.allowed && col.allowed.length) && !col.range" class="text-muted">—</span>
              </td>
              <td class="small">{{ col.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="loading" class="alert alert-info" role="status" aria-live="polite">
      <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> {{ t('features.zaku_pos_upload.status.validating') }}
    </div>

    <div v-if="serverError" class="alert alert-danger" role="alert">
      {{ t('features.zaku_pos_upload.status.server_error', { message: serverError }) }}
    </div>

    <div v-if="result && !loading">
      <div v-if="result.header_errors.length" class="alert alert-danger" role="alert">
        <strong>{{ t('features.zaku_pos_upload.status.header_problems') }}</strong>
        <ul class="mb-0">
          <li v-for="(error, i) in result.header_errors" :key="i">{{ error }}</li>
        </ul>
      </div>

      <div v-else-if="result.row_errors.length" class="alert alert-warning" role="alert" aria-live="polite">
        <div class="d-flex align-items-center mb-2">
          <strong>{{ t('features.zaku_pos_upload.status.rows_skipped', { count: invalidRowCount }) }}</strong>
          <button
            type="button"
            class="btn btn-sm ml-auto"
            :class="pendingEditCount > 0 ? 'btn-primary' : 'btn-outline-secondary'"
            :disabled="loading"
            @click="validate"
          >
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }" aria-hidden="true"></i>
            {{ t('features.zaku_pos_upload.revalidate') }}
            <span v-if="pendingEditCount > 0" class="badge badge-light ml-1">{{ pendingEditCount }}</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-bordered mb-0">
            <thead>
              <tr>
                <th style="width: 60px">{{ t('features.zaku_pos_upload.errors_table.row') }}</th>
                <th style="width: 140px">{{ t('features.zaku_pos_upload.errors_table.sku') }}</th>
                <th style="width: 160px">{{ t('features.zaku_pos_upload.errors_table.column') }}</th>
                <th style="width: 220px">{{ t('features.zaku_pos_upload.errors_table.fix') }}</th>
                <th>{{ t('features.zaku_pos_upload.errors_table.message') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(err, i) in result.row_errors" :key="i">
                <td>{{ err.row_number }}</td>
                <td>
                  <code v-if="err.sku" translate="no">{{ err.sku }}</code>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <code v-if="err.column" translate="no">{{ err.column }}</code>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <template v-if="err.column">
                    <select
                      v-if="allowedValuesFor(err.column).length"
                      class="form-control form-control-sm"
                      :class="{ 'border-warning': isEditPending(err.row_number, err.column) }"
                      :aria-label="t('features.zaku_pos_upload.errors_table.cell_aria_label', { row: err.row_number, column: err.column })"
                      :value="valueFor(err.row_number, err.column)"
                      @change="onCellEdit(err.row_number, err.column, $event.target.value)"
                    >
                      <option value="">—</option>
                      <option v-for="value in allowedValuesFor(err.column)" :key="value" :value="value">{{ value }}</option>
                    </select>
                    <input
                      v-else
                      type="text"
                      autocomplete="off"
                      spellcheck="false"
                      class="form-control form-control-sm"
                      :class="{ 'border-warning': isEditPending(err.row_number, err.column) }"
                      :inputmode="inputHintFor(err.column).inputmode"
                      :pattern="inputHintFor(err.column).pattern"
                      :maxlength="inputHintFor(err.column).maxlength"
                      :aria-label="t('features.zaku_pos_upload.errors_table.cell_aria_label', { row: err.row_number, column: err.column })"
                      :value="valueFor(err.row_number, err.column)"
                      @input="onCellEdit(err.row_number, err.column, $event.target.value)"
                    >
                  </template>
                  <span v-else class="text-muted small">{{ t('features.zaku_pos_upload.errors_table.no_specific_column') }}</span>
                </td>
                <td>{{ err.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-if="!result.header_errors.length && result.rows.length"
        class="alert py-2"
        :class="summaryClass"
        role="status"
        aria-live="polite"
      >
        <strong class="text-success">
          <span aria-hidden="true">✓</span>
          {{ t('features.zaku_pos_upload.status.summary.valid', { count: validRowCount }) }}
        </strong>
        <span class="mx-1" aria-hidden="true">·</span>
        <strong class="text-danger">
          <span aria-hidden="true">✕</span>
          {{ t('features.zaku_pos_upload.status.summary.invalid', { count: invalidRowCount }) }}
        </strong>
        <span v-if="validRowCount === 0" class="ml-2">
          {{ t('features.zaku_pos_upload.status.summary.nothing_importable') }}
        </span>
      </div>

      <div v-if="result.rows.length" class="card mb-3">
        <div class="card-header py-2 d-flex flex-wrap align-items-center">
          <strong class="mr-3">{{ t('features.zaku_pos_upload.preview.title') }}</strong>
          <div class="btn-group btn-group-sm mr-3" role="group">
            <button
              v-for="chip in filterChips"
              :key="chip.value"
              type="button"
              class="btn"
              :class="filter === chip.value ? 'btn-primary' : 'btn-outline-secondary'"
              @click="filter = chip.value"
            >
              {{ chip.label }} ({{ chip.count }})
            </button>
          </div>
          <input
            v-model="search"
            type="search"
            name="zaku_pos_preview_search"
            autocomplete="off"
            spellcheck="false"
            class="form-control form-control-sm ml-auto"
            :aria-label="t('features.zaku_pos_upload.preview.search_placeholder')"
            :placeholder="t('features.zaku_pos_upload.preview.search_placeholder')"
            style="max-width: 240px"
          >
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-bordered table-striped mb-0">
            <thead>
              <tr>
                <th>{{ t('features.zaku_pos_upload.preview.headers.status') }}</th>
                <th>{{ t('features.zaku_pos_upload.preview.headers.row') }}</th>
                <th>{{ t('features.zaku_pos_upload.preview.headers.type') }}</th>
                <th>{{ t('features.zaku_pos_upload.preview.headers.action') }}</th>
                <th>{{ t('features.zaku_pos_upload.preview.headers.sku') }}</th>
                <th>{{ t('features.zaku_pos_upload.preview.headers.title') }}</th>
                <th>{{ t('features.zaku_pos_upload.preview.headers.price_jpy') }}</th>
                <th>{{ t('features.zaku_pos_upload.preview.headers.inventory') }}</th>
                <th>{{ t('features.zaku_pos_upload.preview.headers.issues') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pagedRows" :key="row.row_number" :class="{ 'table-warning': !row.valid }">
                <td class="text-center">
                  <span v-if="row.valid" class="text-success" :title="t('features.zaku_pos_upload.preview.chips.valid')">
                    <span aria-hidden="true">✓</span>
                    <span class="sr-only">{{ t('features.zaku_pos_upload.preview.chips.valid') }}</span>
                  </span>
                  <span v-else class="text-danger" :title="t('features.zaku_pos_upload.preview.chips.invalid')">
                    <span aria-hidden="true">✕</span>
                    <span class="sr-only">{{ t('features.zaku_pos_upload.preview.chips.invalid') }}</span>
                  </span>
                </td>
                <td>{{ row.row_number }}</td>
                <td>
                  <span v-if="row.row_type === 'shopify_product'" class="badge badge-primary">{{ t('features.zaku_pos_upload.row_types.product') }}</span>
                  <span v-else-if="row.row_type === 'variant'" class="badge badge-secondary">{{ t('features.zaku_pos_upload.row_types.variant') }}</span>
                  <span v-else-if="row.row_type === 'inventory_update'" class="badge badge-info">{{ t('features.zaku_pos_upload.row_types.inventory_update') }}</span>
                  <span v-else class="badge badge-danger">{{ t('features.zaku_pos_upload.row_types.unknown') }}</span>
                </td>
                <td>
                  <span v-if="row.action === 'create'" class="badge badge-success">{{ t('features.zaku_pos_upload.actions.create') }}</span>
                  <span v-else-if="row.action === 'update'" class="badge badge-info">{{ t('features.zaku_pos_upload.actions.update') }}</span>
                  <span v-else class="badge badge-warning">{{ row.action }}</span>
                </td>
                <td><code translate="no">{{ row.sku || row.variant_sku_suffix }}</code></td>
                <td>{{ row.shopify_title }}</td>
                <td>
                  <span v-if="row.shopify_sell_price_in_yen">¥{{ row.shopify_sell_price_in_yen }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <span v-if="row.inventory_quantity != null">{{ row.inventory_quantity }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="small">
                  <ul v-if="row.issues && row.issues.length" class="list-unstyled mb-0 text-danger">
                    <li v-for="(issue, i) in row.issues" :key="i">{{ issue }}</li>
                  </ul>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
              <tr v-if="filteredRows.length === 0">
                <td colspan="9" class="text-center text-muted">{{ t('features.zaku_pos_upload.preview.no_match') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="totalPages > 1" class="card-footer py-1 d-flex justify-content-between align-items-center">
          <span class="small text-muted">
            {{ t('features.zaku_pos_upload.preview.page_info', { page: page, total: totalPages, count: filteredRows.length }) }}
          </span>
          <div class="btn-group btn-group-sm">
            <button type="button" class="btn btn-outline-secondary" :disabled="page === 1" @click="page--">{{ t('features.zaku_pos_upload.preview.previous') }}</button>
            <button type="button" class="btn btn-outline-secondary" :disabled="page === totalPages" @click="page++">{{ t('features.zaku_pos_upload.preview.next') }}</button>
          </div>
        </div>
      </div>
    </div>

    <form ref="submitForm" :action="createUrl" method="post" enctype="multipart/form-data" class="d-none">
      <input type="hidden" name="authenticity_token" :value="csrfToken">
      <input type="hidden" name="location" :value="selectedLocation">
      <input ref="hiddenFileInput" type="file" name="csv_file">
    </form>

    <div class="d-flex justify-content-end">
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!canSubmit"
        @click="submit"
      >
        <span v-if="submitting"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> {{ t('features.zaku_pos_upload.submit.uploading') }}</span>
        <span v-else-if="result && result.importable">{{ t('features.zaku_pos_upload.submit.process_valid_rows', { count: validRowCount }) }}</span>
        <span v-else-if="result">{{ t('features.zaku_pos_upload.submit.cannot_import') }}</span>
        <span v-else>{{ t('features.zaku_pos_upload.submit.pick_csv_first') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
const PAGE_SIZE = 25

export default {
  name: 'ZakuPosUploader',
  inject: ['i18n'],
  props: {
    schema: { type: Array, required: true },
    validateUrl: { type: String, required: true },
    createUrl: { type: String, required: true },
    csvTemplateUrl: { type: String, required: true },
    csvInventoryTemplateUrl: { type: String, default: null },
    formatGuideUrl: { type: String, default: null },
    locationOptions: { type: Array, default: () => [] },
    csrfToken: { type: String, required: true }
  },
  data() {
    return {
      t: (key, options) => this.i18n.t(key, options),
      file: null,
      result: null,
      loading: false,
      submitting: false,
      serverError: null,
      dragActive: false,
      showAllColumns: false,
      columnsSearch: '',
      filter: 'all',
      search: '',
      page: 1,
      editedRows: {},
      pendingEdits: {},
      selectedLocation: (
        this.locationOptions.find(opt => opt.value === 'kappabashi') ||
        this.locationOptions[0] ||
        { value: 'kappabashi' }
      ).value
    }
  },
  mounted() {
    if (typeof localStorage !== 'undefined' && !localStorage.getItem('zaku-pos-cols-seen')) {
      this.showAllColumns = true
      try { localStorage.setItem('zaku-pos-cols-seen', '1') } catch (_) {}
    }
  },
  computed: {
    requiredColumns() {
      return this.schema.filter(c => c.required).map(c => c.name)
    },
    schemaByName() {
      const map = {}
      for (const col of this.schema) map[col.name] = col
      return map
    },
    filteredSchema() {
      const term = this.columnsSearch.trim().toLowerCase()
      if (!term) return this.schema
      return this.schema.filter(col => {
        const haystack = [col.name, col.description, col.format, col.range, ...(col.allowed || [])]
          .filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(term)
      })
    },
    validRowCount() {
      return this.result?.valid_row_count || 0
    },
    invalidRowCount() {
      return (this.result?.rows || []).filter(r => !r.valid).length
    },
    summaryClass() {
      if (this.validRowCount === 0) return 'alert-danger'
      if (this.invalidRowCount === 0) return 'alert-success'
      return 'alert-light border'
    },
    filterChips() {
      const rows = this.result?.rows || []
      return [
        { value: 'all', label: this.t('features.zaku_pos_upload.preview.chips.all'), count: rows.length },
        { value: 'valid', label: this.t('features.zaku_pos_upload.preview.chips.valid'), count: rows.length - this.invalidRowCount },
        { value: 'invalid', label: this.t('features.zaku_pos_upload.preview.chips.invalid'), count: this.invalidRowCount },
        { value: 'create', label: this.t('features.zaku_pos_upload.preview.chips.create'), count: rows.filter(r => r.action === 'create').length },
        { value: 'update', label: this.t('features.zaku_pos_upload.preview.chips.update'), count: rows.filter(r => r.action === 'update').length }
      ]
    },
    filteredRows() {
      const rows = this.result?.rows || []
      const term = this.search.trim().toLowerCase()
      return rows.filter(row => {
        if (this.filter === 'create' && row.action !== 'create') return false
        if (this.filter === 'update' && row.action !== 'update') return false
        if (this.filter === 'valid' && !row.valid) return false
        if (this.filter === 'invalid' && row.valid) return false
        if (term) {
          const haystack = [row.sku, row.shopify_title, row.variant_sku_suffix].filter(Boolean).join(' ').toLowerCase()
          if (!haystack.includes(term)) return false
        }
        return true
      })
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / PAGE_SIZE))
    },
    pagedRows() {
      const start = (this.page - 1) * PAGE_SIZE
      return this.filteredRows.slice(start, start + PAGE_SIZE)
    },
    canSubmit() {
      return this.file && this.result && this.result.importable && !this.submitting && !this.loading
    },
    hasEdits() {
      return Object.keys(this.editedRows).length > 0
    },
    pendingEditCount() {
      return Object.values(this.pendingEdits).reduce((sum, byCol) => sum + Object.keys(byCol).length, 0)
    }
  },
  watch: {
    filter() { this.page = 1 },
    search() { this.page = 1 }
  },
  methods: {
    allowedValuesFor(columnName) {
      const col = this.schemaByName[columnName]
      return (col && col.allowed) || []
    },
    inputHintFor(columnName) {
      const col = this.schemaByName[columnName]
      if (!col || !col.format) return {}
      if (col.format === 'Exactly 6 digits') return { inputmode: 'numeric', pattern: '\\d{6}', maxlength: 6 }
      if (col.format === 'MMDD, future weekday') return { inputmode: 'numeric', pattern: '\\d{4}', maxlength: 4 }
      if (col.format === 'YYYY, current year or future') return { inputmode: 'numeric', pattern: '\\d{4}', maxlength: 4 }
      return {}
    },
    openFilePicker() {
      this.$refs.fileInput.click()
    },
    activateDropzone() {
      if (!this.file) this.openFilePicker()
    },
    handleFileChange(event) {
      const file = event.target.files[0]
      if (file) this.setFile(file)
    },
    handleDrop(event) {
      this.dragActive = false
      const file = event.dataTransfer.files[0]
      if (file) this.setFile(file)
    },
    setFile(file) {
      this.file = file
      this.result = null
      this.serverError = null
      this.page = 1
      this.resetEdits()
      this.validate()
    },
    clearFile() {
      if (this.hasEdits && !window.confirm(this.t('features.zaku_pos_upload.dropzone.clear_confirm'))) return
      this.file = null
      this.result = null
      this.serverError = null
      this.resetEdits()
      this.$refs.fileInput.value = ''
    },
    resetEdits() {
      this.editedRows = {}
      this.pendingEdits = {}
    },
    formatBytes(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    valueFor(rowNumber, columnName) {
      const edits = this.editedRows[rowNumber]
      if (edits && Object.prototype.hasOwnProperty.call(edits, columnName)) {
        return edits[columnName]
      }
      const row = (this.result?.rows || []).find(r => r.row_number === rowNumber)
      const original = row?.cells?.[columnName]
      return original == null ? '' : String(original)
    },
    isEditPending(rowNumber, columnName) {
      return this.pendingEdits[rowNumber]?.[columnName] === true
    },
    onCellEdit(rowNumber, columnName, value) {
      if (!this.editedRows[rowNumber]) this.editedRows[rowNumber] = {}
      this.editedRows[rowNumber][columnName] = value

      if (!this.pendingEdits[rowNumber]) this.pendingEdits[rowNumber] = {}
      this.pendingEdits[rowNumber][columnName] = true
    },
    csvEscape(value) {
      if (value == null) return ''
      const s = String(value)
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
      return s
    },
    buildEditedCsv() {
      // Keep the uploaded columns so an inventory-only sheet stays inventory-only.
      const headers = (this.result && this.result.headers && this.result.headers.length)
        ? this.result.headers
        : this.schema.map(c => c.name)
      const lines = [headers.map(h => this.csvEscape(h)).join(',')]
      for (const row of this.result?.rows || []) {
        const values = headers.map(name => this.csvEscape(this.valueFor(row.row_number, name)))
        lines.push(values.join(','))
      }
      return lines.join('\n')
    },
    fileToSubmit() {
      if (!this.hasEdits || !this.result) return this.file
      const blob = new Blob([this.buildEditedCsv()], { type: 'text/csv' })
      const filename = this.file?.name || 'edited.csv'
      return new File([blob], filename, { type: 'text/csv' })
    },
    async validate() {
      if (!this.file) return
      this.loading = true
      this.serverError = null
      try {
        const formData = new FormData()
        formData.append('csv_file', this.fileToSubmit())
        const response = await fetch(this.validateUrl, {
          method: 'POST',
          headers: { 'X-CSRF-Token': this.csrfToken, 'Accept': 'application/json' },
          body: formData
        })
        if (!response.ok) {
          this.serverError = `Server returned ${response.status}`
        } else {
          this.result = await response.json()
          this.pendingEdits = {}
        }
      } catch (err) {
        this.serverError = err.message || String(err)
      } finally {
        this.loading = false
      }
    },
    submit() {
      if (!this.canSubmit) return
      this.submitting = true
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(this.fileToSubmit())
      this.$refs.hiddenFileInput.files = dataTransfer.files
      this.$refs.submitForm.submit()
    }
  }
}
</script>

<style scoped>
.columns-panel .example,
.columns-panel .format {
  font-variant-numeric: tabular-nums;
}
.upload-dropzone {
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.upload-dropzone.has-file {
  cursor: default;
}
.upload-dropzone.is-dragging {
  user-select: none;
}
.upload-dropzone:focus-visible {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .upload-dropzone {
    transition: none;
  }
}
</style>
